import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { ib, table } from "../../ui/record";
import { field, refuse, select } from "../clients/forms";

// Who pays on Patient and payer (brief 1E-19, mockup v79 whoPaysCard): the contracts that can pay
// for the patient (Contract, Covers; Add a contract, the patient's own private contract with its
// own bill rate card among them) and By default (Service, Who pays), saved as each one changes.
// One visit or ride is changed on its own record, with a reason. Shown only with the feature "Who
// pays with more than one contract" on: off, the server answers not available and nothing shows.

interface WhoPays {
  patient: { id: string; name: string };
  canEdit: boolean;
  contracts: {
    id: string | null;
    contractId: string;
    clientId: string;
    name: string;
    kind: string;
    /** Its own dates (Thomas 10/29): "From 11/01/2026 to 12/31/2026", or none. */
    dates: string | null;
    covers: string | null;
    episode: boolean;
  }[];
  defaults: { serviceId: string; service: string; contractId: string | null }[];
  canAdd: { value: string; label: string }[];
}

/** Fills `slot` with the Who pays card; empty while the feature is off. */
export async function whoPaysCard(slot: HTMLElement, patientId: string) {
  const res = await api<WhoPays>("GET", `/api/patients/${patientId}/who-pays`);
  if (!res.ok) {
    slot.replaceChildren();
    return;
  }
  draw(res.body);

  function draw(w: WhoPays) {
    const base = `/api/patients/${patientId}`;
    const contracts = table(
      [
        {
          h: "Contract",
          f: (c: WhoPays["contracts"][number]) =>
            el(
              "span",
              {},
              el("b", {}, c.name),
              el("div", { class: "small muted" }, c.dates ? `${c.kind} · ${c.dates}` : c.kind),
            ),
        },
        { h: "Covers", f: (c) => el("span", { class: "small" }, c.covers ?? "") },
      ],
      w.contracts,
      undefined,
      "Contracts",
    );
    const defaults = table(
      [
        { h: "Service", f: (d: WhoPays["defaults"][number]) => d.service },
        {
          h: "Who pays",
          f: (d) => {
            const s = el(
              "select",
              { "aria-label": `Who pays for ${d.service}`, disabled: !w.canEdit },
              ...w.contracts.map((c) =>
                el(
                  "option",
                  { value: c.contractId, selected: c.contractId === d.contractId },
                  c.name,
                ),
              ),
            );
            s.addEventListener("change", async () => {
              s.disabled = true;
              const r = await api<WhoPays>("PUT", `${base}/pay-defaults`, {
                serviceId: d.serviceId,
                contractId: s.value,
              });
              s.disabled = false;
              if (!r.ok) {
                s.value = d.contractId ?? "";
                return toast("That wasn't saved", r.body.error);
              }
              d.contractId = s.value;
              toast(
                "Saved",
                `${d.service}: ${w.contracts.find((c) => c.contractId === s.value)?.name ?? ""}`,
              );
            });
            return s;
          },
        },
      ],
      w.defaults,
      undefined,
      "By default",
    );
    slot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "Paying contracts" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("credit-card"), " Who pays"),
            el(
              "span",
              { class: "small muted" },
              "Each visit, ride and charge goes to one of these",
            ),
          ),
          w.canEdit ? ib("plus", "Add a contract", () => addContract(w)) : el("span"),
        ),
        contracts,
        el("h3", { style: "margin:12px 0 6px" }, "By default"),
        defaults,
        el(
          "p",
          { class: "small muted", style: "margin:6px 0 0" },
          "Change one visit or ride on its own record, with a reason.",
        ),
      ),
    );
  }

  function addContract(w: WhoPays) {
    const pick = select(
      "Contract",
      w.canAdd.map((c) => [c.value, c.label] as const),
      w.canAdd[0]?.value ?? "",
    );
    // A private contract is independent, like a second insurance: its own start, and an end if
    // it has one (Thomas 10/29).
    const from = field("Starts", "", { type: "date" });
    const to = field("Ends (optional)", "", { type: "date" });
    const covers = field("Covers", "", {
      maxlength: "300",
      placeholder: "e.g. Grocery rides, appointments the claim doesn't cover",
    });
    const save = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("plus"),
      " Add the contract",
    );
    const m = openModal({
      title: "Add a contract",
      sub: "A new private contract gets its own dates and its own bill rate card.",
      body: [
        el("div", { class: "form", style: "max-width:none" }, pick.el, from.el, to.el, covers.el),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api<WhoPays>("POST", `/api/patients/${patientId}/contracts`, {
        contractId: pick.input.value,
        from: from.input.value || null,
        to: to.input.value || null,
        covers: covers.input.value,
      });
      save.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast(
        "Contract added",
        pick.input.value === "private" ? "It gets its own bill rate card" : undefined,
      );
      draw(r.body);
    });
  }
}
